/**
 * UsersPage — user-management list, converged onto the shared resource
 * framework (#1419).
 *
 * The former 552-line bespoke `pages/Users.tsx` (hand-rolled QueryDataTable
 * wiring, a Shield `__actions__` column, a client-side search/paginate memo,
 * two page-owned modals) collapses onto `ResourcePage` + `usersResource`. This
 * wrapper OWNS the two modals (InviteModal + RoleEditor) and their state; the
 * resource is built IN-COMPONENT (via `useMemo`) so the "Change Role" row
 * action can be a `mode: 'handoff'` action whose `onAction` is a closure that
 * captured `setEditingUser` — clicking the menu item opens the RoleEditor with
 * no confirm / toast / busy (#1416). The invite entry rides the ResourcePage
 * `headerActions` seam.
 *
 * Permission gate: the `/users` route is AdminGuard-wrapped in App.tsx (parity
 * with the old page — there was no page-internal `canWrite` gate to migrate).
 */
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageContainer, Button, ResourcePage } from '@resource-kit/ui/react';
import { useBetterAuth } from '@hooks/useAuth';
import type { User } from '@/types/api';
import { buildUsersResource } from '../usersResource';
import { InviteModal } from '../components/InviteModal';
import { RoleEditor } from '../components/RoleEditor';

export default function UsersPage() {
  const { user: currentUser } = useBetterAuth();
  const { t } = useTranslation();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [roleEditorOpen, setRoleEditorOpen] = useState(false);
  // Sticky post-invite confirmation banner — carried verbatim from the old
  // page (a deliberate inline banner, not a transient toast). It persists until
  // the next navigation, matching prior behaviour; kept intentionally on
  // convergence rather than swapped for the toast channel.
  const [feedback, setFeedback] = useState('');

  // The handoff closure the "Change Role" row action hands control back to.
  // Stable identity so the in-component resource memo does not rebuild per render.
  const openRoleEditor = useCallback((user: User) => {
    setEditingUser(user);
    setRoleEditorOpen(true);
  }, []);

  const resource = useMemo(() => buildUsersResource(openRoleEditor), [openRoleEditor]);

  return (
    <PageContainer>
      {feedback && (
        <div className="rounded-md bg-accent/10 border border-accent/20 px-4 py-2 text-accent text-sm">
          {feedback}
        </div>
      )}

      <ResourcePage
        bundle={resource}
        container={false}
        headerActions={
          <Button data-testid="user-invite-button" onClick={() => setInviteOpen(true)}>
            {t('users.inviteUser')}
          </Button>
        }
      />

      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={() => setFeedback(t('users.invitationSent'))}
      />
      {/* Remount per (user, open) so the RoleEditor's draft/query state
          resets on every open without a manual reset effect (mirrors the
          `key=slug` convention in `packages/resource-kit-ui/react/resource`). */}
      <RoleEditor
        key={`${editingUser?.id ?? 'none'}:${roleEditorOpen}`}
        userId={editingUser?.id ?? null}
        userName={editingUser ? editingUser.name || editingUser.email : null}
        userRole={editingUser?.role ?? 'operator'}
        currentUserId={currentUser?.id ?? null}
        open={roleEditorOpen}
        onClose={() => setRoleEditorOpen(false)}
      />
    </PageContainer>
  );
}
